import {
  Box,
  Chip,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import { Button } from '@karehero/llama/components/atoms/Button/Button';

import ShortlistSelect from '@/components/mui/ShortlistSelect';
import StatusChip from '@/cam-admin/paywall/StatusChip';
import { lastMovedOn } from '@/cam-admin/paywall/Shortlist.fixtures';
import {
  isVisibleToFamily,
  type Shortlists,
} from '@/cam-admin/paywall/useShortlists';
import {
  ACTIONS,
  LIST_DIALOG,
  PAGE,
  PUBLISHED,
} from '@/cam-admin/paywall/Shortlist.content';

// The CAM side of the blurred-profile paywall, as a tab in the care circle.
//
// A shortlist belongs to a circle, so it lives beside Calls and Documents rather
// than on a page of its own — the circle header already says whose it is, and the
// tab keeps the CAM in one workspace. Content only: ControlPanel owns the shell,
// the header and the back button, and hosts the row drawer in its overlays slot
// so the drawer covers the whole workspace rather than just this table.
//
// A circle has more than one shortlist, so the tab opens on one and switches
// between them. The selector is unlabelled and leads with the name, because the
// name is what a CAM asks for, with the draft-or-published chip beside it so a
// list can be chosen without opening it. Nothing else: the care type, the town
// and the provider count were all readable from the list once it was open, and
// three lines of metadata per option made a four-item menu a wall.
// Same shortlists as the care search pane, built from its seed items so they
// cannot drift.
//
// One tab, two kinds of list, and the care type decides which. The paywall is dom
// care only: a dom provider is a business that will pay for a lead, so publishing
// can be the send. A care home is rung up by a CAM and has no lead to buy, so a
// care home list here is this same table with the paywall taken out — Publish
// rather than Publish and send, the CAM's own four statuses instead of the payment
// lifecycle, no Visibility column and nothing to send on a row.
//
// Both are in the selector rather than the care home ones being hidden, because a
// CAM works a circle's whole search from this tab. Hiding half of it would make
// the paywall look like how shortlists work, and it is how one kind works.
//
// The lifecycle is the design. A row moves draft → messaged → paid, and
// payment is the one step the CAM does not control. There is deliberately no
// button for it: a control that will never ship was shaping how the whole table
// read, and the paid states are reachable from the scene select instead, which is
// where prototype-only machinery belongs.
//
// Two actions, and never both at once. Publish and send is the whole outbound
// step: it makes the list live to the family and emails the paywall link to every
// provider on it, premium or not — no round of calls in front of it and no tier
// deciding who is worth a link. Because it writes to everybody, it opens a modal
// rather than acting: the words go past a person first.
//
// Then Resend, and Mark secured. Resend is what a published list does most, and
// Mark secured is what ends it: the family arranged care, a CAM heard it on a
// call, and nothing in the system would ever work that out on its own. It leaves
// the list published and the family's copy alone — it is a note that the reason
// for chasing has gone, not a correction to anything.
//
// The row keeps one control, and it is the send: a link to a provider added after
// the list went out, or the resend to a quiet one. Everything that is a judgement
// about a provider belongs to the drawer — Make visible was a primary button
// repeated down the table, which made backing somebody the loudest thing on a
// screen for reading status, and offered the decision without the provider in
// front of you.
//
// What is not here is a Message providers button; it was the same send under a
// second name.

const ShortlistTab = ({ shortlists }: { shortlists: Shortlists }) => {
  const {
    lists,
    current,
    providers,
    isPublished,
    isPaywall,
    isSecured,
    sendable,
    awaitingReply,
    select,
    openCreate,
    openEdit,
    openRowDrawer,
    sendLink,
    resendAll,
    markSecured,
    openPublish,
    openHomePublish,
    setContactStatus,
    dismissProvider,
  } = shortlists;

  // Two columns on a dom list that a care home list has no answer for. Visibility
  // is the payment gate, so without a paywall there is nothing behind it, and the
  // action column holds sends that a care home list never makes.
  const columns = isPaywall ? PAGE.columns : PAGE.careHomeColumns;

  return (
    <Stack spacing={3}>
      {/* Two groups, pushed apart: what list you are looking at on the left, what
          you can do to it on the right. The actions used to sit right beside the
          selector; out at the edge they read as the controls for the table under
          them, which is what they are. */}
      <Stack
        sx={{
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
        }}
        direction='row'
        spacing={3}
        useFlexGap
      >
        <Stack
          sx={{ alignItems: 'center', minWidth: 0 }}
          direction='row'
          spacing={2}
        >
          {/* No field label: the trigger already reads as the name of the thing
              you are looking at, and a label above it would only repeat the tab.
              Wide enough that a full name plus its chip never truncates, which
              also sets the menu width — MUI sizes the menu to the anchor. */}
          <Box sx={{ width: 440 }}>
            <ShortlistSelect
              lists={lists}
              value={current.id}
              onChange={select}
              onCreate={openCreate}
              labels={{
                draft: PAGE.draft,
                published: PUBLISHED.chip,
                create: LIST_DIALOG.menuItem,
              }}
            />
          </Box>
          {/* Beside the selector, same as the Care search preview: it acts on the
              list named next to it, and it is a link rather than a button because
              renaming a list is not one of the two things this tab is for. */}
          <Link
            sx={{ flexShrink: 0 }}
            component='button'
            type='button'
            variant='body2'
            onClick={openEdit}
          >
            {LIST_DIALOG.editLink}
          </Link>
        </Stack>

        {/* What the list can have done to it, and it depends on where the list
            is. A draft has exactly one thing: publish it, which is what sends.
            Coral, because it is the thing a CAM came to the tab to do.

            A published list has two, and they are not the same size. Resend is
            the routine one — chase the silence, same email, same link — so it
            keeps the coral. Mark secured is the rarer one and it ends the search,
            so it sits to the left of it, outlined: a CAM reaching for Resend
            twenty times a day should not have the button that stops everything
            wearing the loud colour next to it.

            Once it is marked there is nothing to press. The care is arranged, so
            the send has nobody left to chase and the slot says the state instead
            of holding a button that would undo it — this is a note about the
            world, not a setting.

            A care home list is the same slot with the paywall taken out of it.
            Publish rather than Publish and send, and a confirm behind it rather
            than the send modal: nothing leaves us, but every home lands in front
            of the family the moment it does, which is the opposite of what the
            dom list next door does. Then no Resend, because
            there was never a send to repeat — chasing a home is a phone call, and
            it is not this tab's job. Mark secured survives both, since care gets
            arranged either way and a CAM hearing so is the only way we find
            out. */}
        <Stack sx={{ alignItems: 'center' }} direction='row' spacing={2}>
          {isSecured ? (
            <Chip size='small' color='limeGreen' label={PAGE.secured} />
          ) : isPublished ? (
            <>
              <Button
                ariaLabel={PAGE.markSecured}
                variant='secondary'
                size='sm'
                onPress={markSecured}
              >
                {PAGE.markSecured}
              </Button>
              {/* Coral on a dom list, where it is the routine job. On a care home
                  list it is absent rather than disabled: a disabled Resend says
                  there is nobody to chase right now, and the truth is that this
                  kind of list has no send to repeat at all. */}
              {isPaywall && (
                <Button
                  ariaLabel={PAGE.resend}
                  className='coral'
                  size='sm'
                  isDisabled={awaitingReply === 0}
                  onPress={resendAll}
                >
                  {PAGE.resend}
                </Button>
              )}
            </>
          ) : isPaywall ? (
            <Button
              ariaLabel={PAGE.publish}
              className='coral'
              size='sm'
              isDisabled={!sendable}
              onPress={openPublish}
            >
              {PAGE.publish}
            </Button>
          ) : (
            <Button
              ariaLabel={PAGE.publishOnly}
              className='coral'
              size='sm'
              isDisabled={!sendable}
              onPress={openHomePublish}
            >
              {PAGE.publishOnly}
            </Button>
          )}
        </Stack>
      </Stack>

      <Paper variant='outlined'>
        <Table>
          <TableHead>
            <TableRow>
              {columns.map((h, i) => (
                <TableCell
                  key={h || `col-${i}`}
                  align={i === columns.length - 1 ? 'right' : 'left'}
                >
                  {h}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {/* A list made from the selector starts with nobody on it. One cell
                across the table rather than a row of blanks, and it says where
                providers come from — the table being empty is not news to the
                person looking at it. */}
            {providers.length === 0 && (
              <TableRow>
                <TableCell colSpan={columns.length}>
                  <Typography variant='body2' color='text.secondary'>
                    {isPaywall ? PAGE.empty : PAGE.careHomeEmpty}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {providers.map((p) => (
              <TableRow
                key={p.id}
                hover
                onClick={() => openRowDrawer(p.id)}
                sx={{ cursor: 'pointer' }}
              >
                {/* The name, and nothing under it. Whether the family can see a
                    provider had a caption here; it made every row on a published
                    list two lines tall to repeat what the status column and the
                    visibility column both already say. */}
                <TableCell>
                  <Typography variant='body1'>{p.name}</Typography>
                </TableCell>

                {/* Read-only on purpose. None of the four states is something a
                    CAM sets: the email going out, Stripe confirming and the
                    provider saying no are what move it. A dropdown here would
                    teach the wrong model of who is in control.
                    The chip is the whole cell. A declining provider is not asked
                    why, so there is no second line under it.
                    On a care home list the chip reads the CAM's own four words
                    instead, because on that list a CAM really is the one who
                    moves it — they made the call. So it is the menu here, not a
                    flat chip: the same four values, set the same way as in Care
                    search, without leaving the list to record what a call just
                    said. The setters are only passed on a care home row, which
                    is what keeps a dom row read-only. */}
                <TableCell>
                  <StatusChip
                    state={p.state}
                    contactState={p.contactState}
                    isPublished={isPublished}
                    onContactStatus={
                      isPaywall ? undefined : (s) => setContactStatus(p.id, s)
                    }
                    onDismiss={
                      isPaywall ? undefined : () => dismissProvider(p.id)
                    }
                  />
                </TableCell>

                {/* Whether the family can see this row. Every row on a draft
                    reads Hidden, which is correct rather than noise: nothing on an
                    unpublished list has reached anybody. Visible carries the
                    stronger colour so the first row to turn — the provider who
                    paid, or the one a CAM let through — stands out down the
                    column.
                    Absent on a care home list. Visibility is the payment gate, and
                    with no payment every home on a published list is in front of
                    the family — the column would read Visible all the way down and
                    answer a question nobody is asking. */}
                {isPaywall && (
                  <TableCell>
                    <Typography
                      variant='body2'
                      color={
                        isPublished && isVisibleToFamily(p)
                          ? 'text.primary'
                          : 'text.secondary'
                      }
                    >
                      {isPublished && isVisibleToFamily(p)
                        ? PUBLISHED.visible
                        : PUBLISHED.hidden}
                    </Typography>
                  </TableCell>
                )}

                {/* The date the row last moved. Blank where nothing has happened
                    yet: a dash would be a value, and the status next to it
                    already says why there is no date. */}
                <TableCell>
                  <Typography variant='body2' color='text.secondary'>
                    {lastMovedOn(p) ?? ''}
                  </Typography>
                </TableCell>

                {/* The row's own controls stop the click here: sending a link
                    should not also open the drawer behind it. */}
                <TableCell align='right' onClick={(e) => e.stopPropagation()}>
                  {/* One Stack for every state, because the cell's align='right'
                      cannot do this job: llama's Button is display:flex unless
                      it is asked to be inline, so on its own it fills the cell
                      and text-align has nothing to move. The Stack is what pins
                      these to the right edge. */}
                  {/* spacing={2} is 16px here. This app resets spacing to MUI's
                      8px scale in theme.tsx — llama's own scale is the
                      non-linear spacingTokens array, where 2 would be 12px. */}
                  <Stack
                    sx={{ justifyContent: 'flex-end', alignItems: 'center' }}
                    direction='row'
                    spacing={2}
                  >
                    {/* Outlined, like Resend. Sending one link is the routine job
                        on a row — the provider added after the list went out —
                        and Publish and send is what does it for the whole list,
                        so a filled button here would be the loudest thing on the
                        table.
                        Both of these are the paywall link, so a care home row has
                        neither: there is no link to send a home. The column stays
                        in the header as an empty last cell rather than being
                        dropped, because the table is the same table and losing a
                        column would reflow every other one. */}
                    {isPaywall && p.state === 'draft' && (
                      <Button
                        ariaLabel={`${ACTIONS.send} to ${p.name}`}
                        variant='secondary'
                        size='sm'
                        onPress={() => sendLink(p.id)}
                      >
                        {ACTIONS.send}
                      </Button>
                    )}
                    {isPaywall && p.state === 'messaged' && (
                      <Button
                        ariaLabel={`${ACTIONS.resend} to ${p.name}`}
                        variant='secondary'
                        size='sm'
                        onPress={() => sendLink(p.id)}
                      >
                        {ACTIONS.resend}
                      </Button>
                    )}
                    {/* Make visible is not here. Backing a provider the family
                        cannot see is a decision about that provider, and it is
                        made in the drawer with their profile, their status and
                        the rule in front of you — not from a table where it was
                        a primary button on four rows at once, one click away
                        from a mis-tap, saying nothing about who it was for. */}
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    </Stack>
  );
};

ShortlistTab.displayName = 'ShortlistTab';
export default ShortlistTab;
